import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table, Tag, Button, Input, Select, DatePicker, Space, Tooltip,
  Modal, Typography, Card, Row, Col, Statistic, Badge,
} from "antd";
import {
  SearchOutlined, ReloadOutlined, EyeOutlined,
  LaptopOutlined, MobileOutlined, TabletOutlined, GlobalOutlined,
  UserOutlined, TeamOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ReactApexChart from "react-apexcharts";
import { fetchUserSessions, fetchSessionStats, fetchDistinctUsers } from "../../../core/data/redux/slices/UserSessionsSlice";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const DEVICE_ICON = { desktop: <LaptopOutlined />, mobile: <MobileOutlined />, tablet: <TabletOutlined />, unknown: <GlobalOutlined /> };
const DEVICE_COLOR = { desktop: "blue", mobile: "green", tablet: "orange", unknown: "default" };

// ── Charts ────────────────────────────────────────────────────────────────────
function DeviceDonut({ data }) {
  if (!data?.length) return <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No data</div>;
  const colorMap = { desktop: "#3b82f6", mobile: "#22c55e", tablet: "#f97316", unknown: "#9ca3af" };
  return (
    <ReactApexChart
      type="donut"
      height={220}
      options={{
        labels: data.map((d) => d._id || "unknown"),
        colors: data.map((d) => colorMap[d._id] || "#6366f1"),
        legend: { position: "bottom", fontSize: "12px" },
        dataLabels: { enabled: true, formatter: (v) => `${v.toFixed(0)}%` },
        plotOptions: { pie: { donut: { size: "60%" } } },
        tooltip: { y: { formatter: (v) => `${v} sessions` } },
      }}
      series={data.map((d) => d.count)}
    />
  );
}

function BrowserBar({ data }) {
  if (!data?.length) return <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No data</div>;
  return (
    <ReactApexChart
      type="bar"
      height={220}
      options={{
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        colors: ["#6366f1"],
        xaxis: { categories: data.map((d) => d._id || "Unknown") },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `${v} sessions` } },
        grid: { borderColor: "#f1f5f9" },
      }}
      series={[{ name: "Sessions", data: data.map((d) => d.count) }]}
    />
  );
}

function OsBar({ data }) {
  if (!data?.length) return <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No data</div>;
  return (
    <ReactApexChart
      type="bar"
      height={220}
      options={{
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        colors: ["#22c55e"],
        xaxis: { categories: data.map((d) => d._id || "Unknown") },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `${v} sessions` } },
        grid: { borderColor: "#f1f5f9" },
      }}
      series={[{ name: "Sessions", data: data.map((d) => d.count) }]}
    />
  );
}

function TopUsersBar({ data }) {
  if (!data?.length) return <div style={{ height: 220, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af" }}>No data</div>;
  return (
    <ReactApexChart
      type="bar"
      height={220}
      options={{
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        colors: ["#f59e0b"],
        xaxis: { categories: data.map((d) => d._id || "Unknown") },
        dataLabels: { enabled: false },
        tooltip: { y: { formatter: (v) => `${v} logins` } },
        grid: { borderColor: "#f1f5f9" },
      }}
      series={[{ name: "Logins", data: data.map((d) => d.count) }]}
    />
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function AdminUserSessions() {
  const dispatch = useDispatch();
  const { sessions, total, loading, stats, statsLoading, distinctUsers } = useSelector((s) => s.userSessions);

  const [filters, setFilters] = useState({
    page: 1, limit: 50,
    userId: undefined, userRole: undefined, deviceType: undefined,
    search: "", startDate: undefined, endDate: undefined,
  });
  const [detailSession, setDetailSession] = useState(null);

  // Build clean params object, stripping undefined/empty values
  const buildParams = (state) => {
    const params = { ...state };
    Object.keys(params).forEach((k) => { if (params[k] === undefined || params[k] === "") delete params[k]; });
    return params;
  };

  // Strip page/limit so stats aggregation is not affected by pagination
  const buildStatsParams = (state) => {
    const { page, limit, ...rest } = buildParams(state);
    return rest;
  };

  const applyFilters = useCallback((overrides = {}) => {
    const merged = { ...filters, ...overrides };
    dispatch(fetchUserSessions(buildParams(merged)));
  }, [dispatch, filters]);

  useEffect(() => {
    dispatch(fetchDistinctUsers());
    dispatch(fetchSessionStats({}));
    applyFilters();
  }, []);

  // Called for non-pagination filter changes — refreshes both table AND charts
  const updateFilter = (key, value) => {
    const updated = { ...filters, [key]: value, page: 1 };
    setFilters(updated);
    dispatch(fetchUserSessions(buildParams(updated)));
    dispatch(fetchSessionStats(buildStatsParams(updated)));
  };

  // Date range — also a filter, refreshes both
  const handleDateRange = (dates) => {
    const updated = { ...filters, startDate: dates?.[0]?.toISOString(), endDate: dates?.[1]?.toISOString(), page: 1 };
    setFilters(updated);
    dispatch(fetchUserSessions(buildParams(updated)));
    dispatch(fetchSessionStats(buildStatsParams(updated)));
  };

  // Pagination only — table changes, charts stay
  const handlePageChange = (page, pageSize) => {
    const updated = { ...filters, page, limit: pageSize };
    setFilters(updated);
    dispatch(fetchUserSessions(buildParams(updated)));
  };

  const columns = [
    {
      title: "Login Time",
      dataIndex: "loginAt",
      width: 155,
      render: (v) => <span style={{ fontSize: 12, color: "#6b7280" }}>{dayjs(v).format("DD MMM YY HH:mm:ss")}</span>,
    },
    {
      title: "User",
      dataIndex: "userName",
      width: 170,
      ellipsis: true,
      render: (name, rec) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 12 }}>{name || "—"}</Text>
          {rec.userEmail && <Text style={{ fontSize: 11, color: "#9ca3af" }}>{rec.userEmail}</Text>}
        </Space>
      ),
    },
    {
      title: "Role",
      dataIndex: "userRole",
      width: 110,
      render: (role) =>
        role ? (
          <Tag color={role === "superadmin" ? "purple" : role === "companyAdmin" ? "cyan" : "default"} style={{ fontSize: 11 }}>
            {role}
          </Tag>
        ) : "—",
    },
    {
      title: "Device",
      dataIndex: "deviceType",
      width: 95,
      render: (type) => (
        <Tag color={DEVICE_COLOR[type] || "default"} icon={DEVICE_ICON[type]}>
          {type || "unknown"}
        </Tag>
      ),
    },
    {
      title: "Browser",
      dataIndex: "browser",
      width: 140,
      ellipsis: true,
      render: (v) => <Text style={{ fontSize: 12 }}>{v || "—"}</Text>,
    },
    {
      title: "OS",
      dataIndex: "os",
      width: 130,
      ellipsis: true,
      render: (v) => <Text style={{ fontSize: 12 }}>{v || "—"}</Text>,
    },
    {
      title: "Screen",
      dataIndex: "screenWidth",
      width: 105,
      render: (w, rec) =>
        w && rec.screenHeight ? (
          <Text style={{ fontSize: 12 }}>{w}×{rec.screenHeight}</Text>
        ) : "—",
    },
    {
      title: "IP",
      dataIndex: "ip",
      width: 115,
      render: (ip) => <Text style={{ fontSize: 12, color: "#6b7280" }}>{ip || "—"}</Text>,
    },
    {
      title: "Timezone",
      dataIndex: "timezone",
      width: 160,
      ellipsis: true,
      render: (tz) => <Text style={{ fontSize: 12 }}>{tz || "—"}</Text>,
    },
    {
      title: "",
      width: 50,
      render: (_, record) => (
        <Tooltip title="View details">
          <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setDetailSession(record)} />
        </Tooltip>
      ),
    },
  ];

  const totalSessions = stats ? (stats.deviceBreakdown || []).reduce((acc, d) => acc + d.count, 0) : total;
  const mobileCount = stats ? (stats.deviceBreakdown || []).find((d) => d._id === "mobile")?.count || 0 : 0;
  const desktopCount = stats ? (stats.deviceBreakdown || []).find((d) => d._id === "desktop")?.count || 0 : 0;
  const topUser = stats?.topUsers?.[0];

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <div>
            <h3 style={{ margin: 0, fontWeight: 700 }}>User Sessions</h3>
            <span style={{ color: "#6b7280" }}>Login activity with device & browser details</span>
          </div>
          <Button icon={<ReloadOutlined />} onClick={() => { dispatch(fetchSessionStats(buildStatsParams(filters))); applyFilters(); }}>Refresh</Button>
        </div>

        {/* KPI cards */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          {[
            { title: "Total Sessions", value: totalSessions, icon: <GlobalOutlined />, color: "#6366f1" },
            { title: "Desktop Logins", value: desktopCount, icon: <LaptopOutlined />, color: "#3b82f6" },
            { title: "Mobile Logins", value: mobileCount, icon: <MobileOutlined />, color: "#22c55e" },
            { title: "Top User", value: topUser ? (topUser.name || topUser._id || "—") : "—", icon: <UserOutlined />, color: "#f59e0b" },
          ].map(({ title, value, icon, color }) => (
            <Col span={6} key={title}>
              <Card size="small" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
                <Statistic
                  title={<span style={{ fontSize: 13, color: "#6b7280" }}>{title}</span>}
                  value={value}
                  prefix={React.cloneElement(icon, { style: { color } })}
                  valueStyle={{ fontSize: 22, fontWeight: 700 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* Charts */}
        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={6}>
            <Card size="small" title="Device Types" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
              <DeviceDonut data={stats?.deviceBreakdown} />
            </Card>
          </Col>
          <Col span={9}>
            <Card size="small" title="Top Browsers" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
              <BrowserBar data={stats?.browserBreakdown} />
            </Card>
          </Col>
          <Col span={9}>
            <Card size="small" title="Operating Systems" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
              <OsBar data={stats?.osBreakdown} />
            </Card>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginBottom: 20 }}>
          <Col span={24}>
            <Card size="small" title="Most Active Users (by login count)" style={{ borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }} loading={statsLoading}>
              <TopUsersBar data={stats?.topUsers} />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <div style={{
          background: "#fff", borderRadius: 10, padding: "14px 18px",
          boxShadow: "0 1px 6px rgba(0,0,0,0.06)", marginBottom: 16,
          display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center",
        }}>
          <Input
            placeholder="Search user, email or IP..."
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            style={{ width: 230 }}
            value={filters.search}
            onChange={(e) => updateFilter("search", e.target.value)}
            allowClear
          />
          <Select
            showSearch
            placeholder="User"
            style={{ width: 200 }}
            allowClear
            value={filters.userId}
            onChange={(v) => updateFilter("userId", v)}
            filterOption={(input, opt) =>
              (opt?.label || "").toLowerCase().includes(input.toLowerCase())
            }
            options={distinctUsers.map((u) => ({
              value: String(u._id),
              label: u.userName || u.userEmail || String(u._id),
            }))}
            optionRender={(opt) => {
              const u = distinctUsers.find((x) => String(x._id) === opt.value);
              return (
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{u?.userName || "—"}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af" }}>{u?.userEmail || ""}</div>
                </div>
              );
            }}
          />
          <Select
            placeholder="Role"
            style={{ width: 145 }}
            allowClear
            value={filters.userRole}
            onChange={(v) => updateFilter("userRole", v)}
            options={[
              { label: "Super Admin", value: "superadmin" },
              { label: "Company Admin", value: "companyAdmin" },
              { label: "Agent", value: "user" },
            ]}
          />
          <Select
            placeholder="Device"
            style={{ width: 120 }}
            allowClear
            value={filters.deviceType}
            onChange={(v) => updateFilter("deviceType", v)}
            options={[
              { label: "Desktop", value: "desktop" },
              { label: "Mobile", value: "mobile" },
              { label: "Tablet", value: "tablet" },
              { label: "Unknown", value: "unknown" },
            ]}
          />
          <RangePicker style={{ width: 240 }} onChange={handleDateRange} disabledDate={(d) => d && d > dayjs().endOf("day")} />
          <Button
            onClick={() => {
              const reset = { page: 1, limit: 50, userId: undefined, userRole: undefined, deviceType: undefined, search: "", startDate: undefined, endDate: undefined };
              setFilters(reset);
              dispatch(fetchUserSessions({ page: 1, limit: 50 }));
              dispatch(fetchSessionStats({}));
            }}
          >
            Reset
          </Button>
          <span style={{ marginLeft: "auto", color: "#6b7280", fontSize: 13 }}>{total.toLocaleString()} sessions</span>
        </div>

        {/* Table */}
        <div style={{ background: "#fff", borderRadius: 10, padding: "0 0 8px", boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
          <Table
            columns={columns}
            dataSource={sessions}
            rowKey="_id"
            loading={loading}
            size="small"
            scroll={{ x: 1100 }}
            pagination={{
              current: filters.page,
              pageSize: filters.limit,
              total,
              showSizeChanger: true,
              pageSizeOptions: ["25", "50", "100"],
              showTotal: (t, [from, to]) => `${from}–${to} of ${t}`,
              onChange: handlePageChange,
            }}
          />
        </div>

        {/* Detail Modal */}
        <Modal
          title={
            <Space>
              {detailSession && DEVICE_ICON[detailSession.deviceType]}
              <Text strong>{detailSession?.userName || "Session Details"}</Text>
            </Space>
          }
          open={!!detailSession}
          onCancel={() => setDetailSession(null)}
          footer={null}
          width={680}
        >
          {detailSession && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div><Text type="secondary">User</Text><br /><Text strong>{detailSession.userName || "—"}</Text></div>
              <div><Text type="secondary">Email</Text><br /><Text>{detailSession.userEmail || "—"}</Text></div>
              <div><Text type="secondary">Role</Text><br />
                <Tag color={detailSession.userRole === "superadmin" ? "purple" : detailSession.userRole === "companyAdmin" ? "cyan" : "default"}>
                  {detailSession.userRole || "—"}
                </Tag>
              </div>
              <div><Text type="secondary">Login Time</Text><br /><Text>{dayjs(detailSession.loginAt).format("DD MMM YYYY, HH:mm:ss")}</Text></div>
              <div><Text type="secondary">IP Address</Text><br /><Text code>{detailSession.ip || "—"}</Text></div>
              <div><Text type="secondary">Device Type</Text><br />
                <Tag color={DEVICE_COLOR[detailSession.deviceType]} icon={DEVICE_ICON[detailSession.deviceType]}>
                  {detailSession.deviceType || "unknown"}
                </Tag>
              </div>
              <div><Text type="secondary">Browser</Text><br /><Text>{detailSession.browser || "—"}</Text></div>
              <div><Text type="secondary">OS</Text><br /><Text>{detailSession.os || "—"}</Text></div>
              {detailSession.deviceVendor && <div><Text type="secondary">Device Vendor</Text><br /><Text>{detailSession.deviceVendor}</Text></div>}
              {detailSession.deviceModel && <div><Text type="secondary">Device Model</Text><br /><Text>{detailSession.deviceModel}</Text></div>}
              <div>
                <Text type="secondary">Screen Resolution</Text><br />
                <Text>{detailSession.screenWidth && detailSession.screenHeight ? `${detailSession.screenWidth} × ${detailSession.screenHeight}` : "—"}</Text>
              </div>
              <div>
                <Text type="secondary">Viewport</Text><br />
                <Text>{detailSession.viewportWidth && detailSession.viewportHeight ? `${detailSession.viewportWidth} × ${detailSession.viewportHeight}` : "—"}</Text>
              </div>
              {detailSession.pixelRatio && <div><Text type="secondary">Pixel Ratio</Text><br /><Text>{detailSession.pixelRatio}x</Text></div>}
              <div><Text type="secondary">Timezone</Text><br /><Text>{detailSession.timezone || "—"}</Text></div>
              <div><Text type="secondary">Language</Text><br /><Text>{detailSession.language || "—"}</Text></div>
              <div style={{ gridColumn: "1 / -1" }}>
                <Text type="secondary">User Agent</Text>
                <pre style={{ background: "#f9fafb", borderRadius: 6, padding: "8px 12px", fontSize: 11, margin: "4px 0 0", overflow: "auto", wordBreak: "break-all", whiteSpace: "pre-wrap" }}>
                  {detailSession.userAgent || "—"}
                </pre>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </div>
  );
}
